// Guests — live, database-backed guest directory.
//
// Pulls /guests for the list (already includes per-row stay_count and
// last_stay from the SQL subqueries).  Selecting a guest opens a detail
// drawer that calls /reservations?guest_id=… for the full stay history
// and totals.  Super admin gets a delete button; everyone else can
// create + edit.
//
// Replaces the old mockData-driven version entirely.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  guestsUrl, guestByIdUrl, reservationsUrl,
} from "../../BaseURL";

const BLANK_FORM = {
  full_name: "", email: "", phone: "",
  id_type: "Ghana Card", id_number: "",
  nationality: "", address: "", notes: "",
};

const ID_TYPES = ["Ghana Card", "Passport", "Driver's Licence", "Voter's ID", "Other"];

export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);          // guest row OR null = closed
  const [form, setForm] = useState(BLANK_FORM);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);        // detail drawer
  const [msg, setMsg] = useState({ type: "", text: "" });

  // Cached user → role check for delete.
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch (_) { return null; }
  }, []);
  const isSuperAdmin = me?.role === "super_admin";

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4500);
  };

  // ── Loaders ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(guestsUrl, { params: search ? { q: search } : {} });
      setGuests(res.data?.results || []);
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Could not load guests");
    } finally {
      setLoading(false);
    }
  }, [search]);

  // Debounce the search input so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // ── Stats ─────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total       = guests.length;
    const withEmail   = guests.filter(g => g.email).length;
    const withPhone   = guests.filter(g => g.phone).length;
    const repeat      = guests.filter(g => Number(g.stay_count) > 1).length;
    const newThisMonth = (() => {
      const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
      return guests.filter(g => g.created_at && new Date(g.created_at) >= monthStart).length;
    })();
    return { total, withEmail, withPhone, repeat, newThisMonth };
  }, [guests]);

  // ── Form actions ──────────────────────────────────────────────────────
  const openCreate = () => {
    setForm({ ...BLANK_FORM });
    setEditing(null);
    setCreating(true);
  };
  const openEdit = (g) => {
    setForm({
      full_name: g.full_name || "",
      email:     g.email || "",
      phone:     g.phone || "",
      id_type:   g.id_type || "Ghana Card",
      id_number: g.id_number || "",
      nationality: g.nationality || "",
      address:   g.address || "",
      notes:     g.notes || "",
    });
    setEditing(g);
    setCreating(true);
  };
  const closeForm = () => { setCreating(false); setEditing(null); setForm(BLANK_FORM); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim()) { showMsg("error", "Full name is required."); return; }
    setSaving(true);
    try {
      if (editing) {
        const res = await axios.put(guestByIdUrl(editing.id), form);
        if (!res.data?.success) { showMsg("error", res.data?.message || "Update failed"); return; }
        showMsg("success", `${form.full_name} updated.`);
      } else {
        const res = await axios.post(guestsUrl, form);
        if (!res.data?.success) { showMsg("error", res.data?.message || "Create failed"); return; }
        showMsg("success", `${form.full_name} added to the guest book.`);
      }
      closeForm();
      await load();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete guest "${g.full_name}"?\n\nThis cannot be undone. If the guest has reservations on file, the server will refuse.`)) return;
    try {
      const res = await axios.delete(guestByIdUrl(g.id));
      if (!res.data?.success) { showMsg("error", res.data?.message || "Delete failed"); return; }
      showMsg("success", `${g.full_name} removed.`);
      if (selected?.id === g.id) setSelected(null);
      await load();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Delete failed");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, color: "#2c1f14", fontWeight: 500, marginBottom: 4 }}>
          Guests
        </div>
        <div style={{ fontSize: 14.5, color: "#6b5142" }}>
          Every guest in the system — pulled live from the database. Add, edit, or pull up a full stay history.
        </div>
      </div>

      {/* Stats */}
      <div className="g4" style={{ marginBottom: 22 }}>
        <Stat icon="👥" label="Total Guests" value={stats.total} sub="On file" />
        <Stat icon="🔁" label="Repeat Guests" value={stats.repeat}
          sub={stats.total > 0 ? `${Math.round((stats.repeat / stats.total) * 100)}% have stayed more than once` : "—"} />
        <Stat icon="📧" label="With Email" value={stats.withEmail}
          sub={stats.total > 0 ? `${Math.round((stats.withEmail / stats.total) * 100)}% reachable` : "—"} />
        <Stat icon="✨" label="New This Month" value={stats.newThisMonth} sub="Added since the 1st" />
      </div>

      {/* Toast */}
      {msg.text && (
        <div style={{
          padding: "11px 16px", borderRadius: 8, marginBottom: 14,
          background: msg.type === "success" ? "#eaf5ee" : "#fef0ee",
          color:      msg.type === "success" ? "#2d6a4f" : "#a93226",
          border:     `1px solid ${msg.type === "success" ? "#a8d5b5" : "#f5c5bc"}`,
          fontSize: 14, fontWeight: 500,
        }}>
          {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <input
          className="f-input"
          style={{ maxWidth: 320, padding: "9px 12px" }}
          placeholder="🔍  Search by name, phone or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn-dark" style={{ marginLeft: "auto" }} onClick={openCreate}>
          + Add Guest
        </button>
      </div>

      {/* Two-column: list + optional detail drawer */}
      <div style={{
        display: "grid", gap: 16,
        gridTemplateColumns: selected ? "minmax(0, 1.4fr) minmax(340px, 1fr)" : "1fr",
      }}>
        <div className="panel">
          <div className="panel-hd">
            <span className="panel-hd-title">Guest Directory</span>
            <span className="badge badge-gold">
              {loading ? "…" : `${guests.length} guest${guests.length === 1 ? "" : "s"}`}
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Guest</th><th>Contact</th><th>ID</th>
                  <th style={{ textAlign: "center" }}>Stays</th>
                  <th>Last stay</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 28, color: "#6b5142" }}>Loading…</td></tr>
                ) : guests.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 28, color: "#6b5142", fontStyle: "italic" }}>
                    {search ? "No guests match that search." : "No guests yet — they'll appear as bookings come in, or use + Add Guest."}
                  </td></tr>
                ) : guests.map(g => {
                  const isSelected = selected?.id === g.id;
                  return (
                    <tr key={g.id}
                      style={{
                        cursor: "pointer",
                        background: isSelected ? "rgba(200,160,90,.10)" : "transparent",
                      }}
                      onClick={() => setSelected(g)}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div className="avatar" style={{ width: 34, height: 34, fontSize: 15 }}>
                            {(g.full_name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500, color: "#2c1f14" }}>{g.full_name}</div>
                            {g.nationality && (
                              <div style={{ fontSize: 11.5, color: "#8a7060" }}>{g.nationality}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {g.phone && <div style={{ fontSize: 13 }}>{g.phone}</div>}
                        {g.email && <div style={{ fontSize: 12, color: "#6b5142" }}>{g.email}</div>}
                        {!g.phone && !g.email && <span style={{ color: "#b8a090" }}>—</span>}
                      </td>
                      <td>
                        {g.id_number ? (
                          <>
                            <div style={{ fontSize: 13 }}>{g.id_number}</div>
                            <div style={{ fontSize: 11, color: "#8a7060" }}>{g.id_type}</div>
                          </>
                        ) : <span style={{ color: "#b8a090" }}>—</span>}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-block", padding: "3px 10px", borderRadius: 999,
                          background: Number(g.stay_count) > 1 ? "rgba(74,124,89,.14)" : "rgba(180,140,80,.10)",
                          color:      Number(g.stay_count) > 1 ? "#2d6a4f" : "#6b5142",
                          fontWeight: 500, fontSize: 12,
                        }}>
                          {g.stay_count || 0}
                        </span>
                      </td>
                      <td>
                        {g.last_stay
                          ? new Date(g.last_stay).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                          : <span style={{ color: "#b8a090" }}>Never</span>}
                      </td>
                      <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                        <button className="btn-outline" style={{ padding: "5px 11px", fontSize: 11 }}
                          onClick={() => openEdit(g)}>Edit</button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => remove(g)}
                            style={{
                              marginLeft: 6, padding: "5px 11px", fontSize: 11,
                              background: "transparent", border: "1.5px solid rgba(192,57,43,.3)",
                              borderRadius: 7, color: "#a93226", cursor: "pointer",
                              fontFamily: "'Jost',sans-serif", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 500,
                            }}>
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {selected && (
          <GuestDetail
            guest={selected}
            onClose={() => setSelected(null)}
            onEdit={() => openEdit(selected)}
            isSuperAdmin={isSuperAdmin}
            onDelete={() => remove(selected)}
          />
        )}
      </div>

      {/* Add / Edit modal */}
      {creating && (
        <div className="modal-overlay" onClick={() => !saving && closeForm()}>
          <div
            className="modal-box"
            style={{ width: "min(560px, 96vw)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <span className="modal-title">{editing ? `Edit ${editing.full_name}` : "Add Guest"}</span>
              <button className="modal-close" onClick={() => !saving && closeForm()}>✕</button>
            </div>
            <form className="modal-body" onSubmit={submit}>
              <div className="f-group">
                <label className="f-label">Full Name *</label>
                <input className="f-input" required autoFocus
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="e.g. Kwame Mensah"/>
              </div>
              <div className="form-grid">
                <div className="f-group">
                  <label className="f-label">Phone</label>
                  <input className="f-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="0244 000 000"/>
                </div>
                <div className="f-group">
                  <label className="f-label">Email</label>
                  <input className="f-input" type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="guest@email.com"/>
                </div>
                <div className="f-group">
                  <label className="f-label">ID Type</label>
                  <select className="f-select" value={form.id_type}
                    onChange={(e) => setForm({ ...form, id_type: e.target.value })}>
                    {ID_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="f-group">
                  <label className="f-label">ID Number</label>
                  <input className="f-input"
                    value={form.id_number}
                    onChange={(e) => setForm({ ...form, id_number: e.target.value })}/>
                </div>
                <div className="f-group">
                  <label className="f-label">Nationality</label>
                  <input className="f-input"
                    value={form.nationality}
                    onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                    placeholder="e.g. Ghanaian"/>
                </div>
                <div className="f-group">
                  <label className="f-label">Address</label>
                  <input className="f-input"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}/>
                </div>
              </div>
              <div className="f-group">
                <label className="f-label">Notes (internal)</label>
                <textarea className="f-input" rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything the front desk should know — preferences, allergies, …"/>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button type="submit" className="btn-dark" style={{ flex: 1 }} disabled={saving}>
                  {saving ? "Saving…" : editing ? "Save changes" : "Add guest"}
                </button>
                <button type="button" className="btn-outline" onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stat tile ─────────────────────────────────────────────────────────────
function Stat({ icon, label, value, sub }) {
  return (
    <div className="stat">
      <div className="stat-icon">{icon}</div>
      <div className="stat-val">{value}</div>
      <div className="stat-lbl">{label}</div>
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

// ── Detail drawer (loads the guest's reservations on demand) ────────────
function GuestDetail({ guest, onClose, onEdit, isSuperAdmin, onDelete }) {
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(reservationsUrl, { params: { guest_id: guest.id } });
        if (!cancelled) setStays(res.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [guest.id]);

  const totals = useMemo(() => {
    const spend = stays.reduce((s, r) => s + (Number(r.paid_amount) || 0), 0);
    const owed  = stays.reduce((s, r) => s + (Number(r.balance) || 0), 0);
    const inHouse = stays.filter(r => r.status === "checked_in").length;
    return { spend, owed, inHouse };
  }, [stays]);

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "—";

  return (
    <div className="panel" style={{ alignSelf: "start", position: "sticky", top: 20 }}>
      {/* Header */}
      <div className="panel-hd">
        <span className="panel-hd-title">Guest profile</span>
        <button onClick={onClose}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 16, color: "#8a7060",
          }}
          aria-label="Close">✕</button>
      </div>

      <div style={{ padding: 18 }}>
        {/* Identity card */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <div className="avatar" style={{ width: 54, height: 54, fontSize: 24 }}>
            {(guest.full_name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "#2c1f14", fontWeight: 500 }}>
              {guest.full_name}
            </div>
            <div style={{ fontSize: 12.5, color: "#6b5142", marginTop: 2 }}>
              {[guest.phone, guest.email].filter(Boolean).join(" · ") || "No contact info on file"}
            </div>
          </div>
        </div>

        {/* Mini-KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
          <Mini label="Stays" value={stays.length} />
          <Mini label="Spent"   value={`GH₵ ${totals.spend.toFixed(2)}`} accent="#2d6a4f" />
          <Mini label="Owed"    value={`GH₵ ${totals.owed.toFixed(2)}`}  accent={totals.owed > 0 ? "#a93226" : "#6b5142"} />
        </div>

        {/* Profile fields */}
        <div style={{ marginBottom: 14 }}>
          {guest.id_number && <Field label={`ID — ${guest.id_type}`} value={guest.id_number} />}
          {guest.nationality && <Field label="Nationality" value={guest.nationality} />}
          {guest.address     && <Field label="Address"     value={guest.address} />}
          {guest.notes       && <Field label="Notes"       value={guest.notes} />}
          <Field label="Added" value={fmtDate(guest.created_at)} />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <button className="btn-gold" onClick={onEdit} style={{ flex: 1, padding: "8px 14px", fontSize: 11.5 }}>
            Edit profile
          </button>
          {isSuperAdmin && (
            <button onClick={onDelete}
              style={{
                padding: "8px 14px", fontSize: 11.5,
                background: "transparent", border: "1.5px solid rgba(192,57,43,.3)",
                borderRadius: 7, color: "#a93226", cursor: "pointer",
                fontFamily: "'Jost',sans-serif", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 500,
              }}>
              Delete
            </button>
          )}
        </div>

        {/* Stay history */}
        <div style={{
          fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase",
          color: "#6b5142", marginBottom: 8, fontWeight: 500,
        }}>
          Stay history ({stays.length})
        </div>
        {loading ? (
          <div style={{ color: "#8a7060", fontSize: 13, padding: "8px 0" }}>Loading…</div>
        ) : stays.length === 0 ? (
          <div style={{ color: "#8a7060", fontSize: 13, fontStyle: "italic", padding: "8px 0" }}>
            No stays on record yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
            {stays.map(r => (
              <div key={r.id} style={{
                padding: "10px 12px", borderRadius: 8,
                background: "#fdf8f2",
                border: "1px solid rgba(180,140,80,.18)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, color: "#2c1f14", fontWeight: 500 }}>
                    {r.code}
                  </span>
                  <StatusPill status={r.status}/>
                </div>
                <div style={{ fontSize: 12, color: "#6b5142", marginTop: 4 }}>
                  Room {r.room_number || "—"} · {fmtDate(r.check_in_at || r.check_in_date)} → {fmtDate(r.check_out_at || r.check_out_date)}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 12 }}>
                  <span style={{ color: "#3a2a1a" }}>
                    Total <strong>GH₵ {Number(r.total_amount).toFixed(2)}</strong>
                  </span>
                  <span style={{ color: Number(r.balance) > 0 ? "#a93226" : "#2d6a4f", fontWeight: 500 }}>
                    {Number(r.balance) > 0
                      ? `Owes GH₵ ${Number(r.balance).toFixed(2)}`
                      : "Settled"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value, accent = "#c8a05a" }) {
  return (
    <div style={{
      padding: "8px 10px", borderRadius: 8,
      border: "1px solid rgba(180,140,80,.22)",
      background: "#fdf8f2",
      borderLeft: `3px solid ${accent}`,
    }}>
      <div style={{ fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "#6b5142", fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, color: "#2c1f14", fontWeight: 500, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "#8a7060", fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#2c1f14", marginTop: 1 }}>{value}</div>
    </div>
  );
}

const STATUS_COLOR = {
  pending: "#3a7bd5", confirmed: "#c8a05a", checked_in: "#4a7c59",
  checked_out: "#8a7060", cancelled: "#a93226", no_show: "#7a4a4a",
};
function StatusPill({ status }) {
  const color = STATUS_COLOR[status] || "#8a7060";
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      background: `${color}22`, color, fontSize: 10, fontWeight: 500,
      letterSpacing: ".08em", textTransform: "uppercase",
    }}>
      {String(status || "").replace("_", " ")}
    </span>
  );
}
